import LegalPageShell from "./LegalPageShell";

/**
 * Privacy Policy for Rate-ED. Aligned with India's Digital Personal Data Protection Act
 * (DPDP Act, 2023). Good-faith draft — please have your lawyer review before public launch.
 */
export default function Privacy() {
  return (
    <LegalPageShell
      title="Privacy Policy"
      effectiveDate="July 2026"
      testId="privacy-page"
    >
      <Section title="1. Who We Are">
        Rate-ED (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is the data fiduciary
        responsible for the personal data collected on the Platform. This Policy explains
        what we collect, how we use it, and the rights you have under the Digital Personal
        Data Protection Act, 2023 (&ldquo;DPDP Act&rdquo;).
      </Section>

      <Section title="2. Data We Collect">
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><b>Account data</b>: name, email, hashed password, role (Explorer / Mentor).</li>
          <li><b>Explorer profile data</b>: optional child details (first name, grade, school) — used only to display session context to the assigned mentor.</li>
          <li><b>Mentor profile data</b>: bio, qualifications, experience, subjects taught, hourly fees, phone number, city, and uploaded documents (photo, Aadhar, degree). Documents are stored securely in Supabase Storage.</li>
          <li><b>Transaction data</b>: booking, enrollment, payment identifiers, wallet balances, payouts.</li>
          <li><b>Session data</b>: session start/end timestamps, punch-ins, ratings, reviews, session reports.</li>
          <li><b>Usage data</b>: IP address, browser type, and page-level events (via PostHog) used to improve the Platform.</li>
        </ul>
      </Section>

      <Section title="3. How We Use Your Data">
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>To create and manage your account and deliver core Platform features.</li>
          <li>To match Explorers with suitable Mentors and enable bookings and payments.</li>
          <li>To send transactional emails (payment receipts, booking reminders, session updates).</li>
          <li>To detect and prevent fraud, abuse, and unauthorised access.</li>
          <li>To comply with legal obligations (GST, tax filings, law-enforcement requests).</li>
          <li>To improve the Platform (analytics on aggregated, non-identifiable data).</li>
        </ul>
      </Section>

      <Section title="4. Sensitive Data — Aadhar &amp; ID Proofs">
        Aadhar and other ID documents uploaded by Mentors are:
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Stored in a private storage bucket, not directly downloadable via a public URL.</li>
          <li>Marked <b>private</b> at the server level by default. Even if a Mentor marks other documents public, Aadhar remains hidden.</li>
          <li>Accessible only to authorised Rate-ED admins for verification purposes.</li>
          <li>Never displayed on public mentor profiles or shared with Explorers.</li>
        </ul>
      </Section>

      <Section title="5. Third-Party Processors">
        We rely on the following data processors, each bound by their own privacy commitments:
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><b>Razorpay</b> (India) — payment processing.</li>
          <li><b>Supabase</b> (US / EU) — document storage.</li>
          <li><b>Resend</b> (US) — transactional email delivery.</li>
          <li><b>MongoDB Atlas</b> (India region where available) — primary database.</li>
          <li><b>Render</b> (US) — application hosting.</li>
          <li><b>PostHog</b> (self-hosted or EU) — product analytics.</li>
        </ul>
      </Section>

      <Section title="6. Cookies &amp; Similar Technologies">
        We use essential cookies to keep you signed in (JWT session cookies) and for
        security (CSRF-like tokens where applicable). We do NOT use third-party advertising
        cookies. Analytics are anonymised at the ingest stage.
      </Section>

      <Section title="7. Data Retention">
        We retain personal data only for as long as necessary to deliver the Service or as
        required by law. Specifically:
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Account data: for the lifetime of your account.</li>
          <li>Financial records (invoices, payouts): 8 years, as required by Indian tax law.</li>
          <li>Session reports: 3 years after closure.</li>
          <li>Deleted-account data: anonymised within 30 days of an account-deletion request, except where retention is required by law.</li>
        </ul>
      </Section>

      <Section title="8. Your Rights (DPDP Act)">
        You have the right to:
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><b>Access</b> a machine-readable export of the personal data we hold about you.</li>
          <li><b>Correct or update</b> inaccurate data (via profile settings or by email).</li>
          <li><b>Delete</b> your account and associated data, subject to lawful retention.</li>
          <li><b>Withdraw consent</b> to marketing communications at any time.</li>
          <li><b>Grievance redressal</b> — contact our Data Protection Officer at admin@rateed.com. We will respond within 15 working days.</li>
        </ul>
        To exercise these rights, use the in-app &ldquo;Account &amp; Privacy&rdquo; settings or
        email <a href="mailto:admin@rateed.com" className="font-bold underline">admin@rateed.com</a>.
      </Section>

      <Section title="9. Security">
        We implement industry-standard controls including bcrypt password hashing, JWT
        token versioning for instant session invalidation, TLS-encrypted transport, rate
        limiting on authentication endpoints, IP-based lockout on repeated failures, a
        strict Content-Security-Policy, log-redaction of secrets, and audit logging of
        admin actions. Despite this, no service can guarantee absolute security &mdash; we
        will notify affected users promptly in the event of a personal-data breach.
      </Section>

      <Section title="10. Children">
        Explorer accounts are intended for adults (18+). Child details entered by an
        Explorer are stored solely to provide session context to the assigned Mentor and
        are never used for marketing.
      </Section>

      <Section title="11. Cross-Border Transfers">
        Some of our processors (Supabase, Resend, Render) may store data outside India in
        jurisdictions with equivalent or stronger data-protection standards. We rely on
        contractual safeguards to protect your data during any cross-border transfer.
      </Section>

      <Section title="12. Changes to this Policy">
        We may update this Policy from time to time. Material changes will be notified
        in-app. The effective date at the top of this page reflects the current version.
      </Section>

      <Section title="13. Contact / Grievance Officer">
        Data Protection Officer: <b>Rate-ED Support</b><br />
        Email: <a href="mailto:admin@rateed.com" className="font-bold underline">admin@rateed.com</a>
      </Section>
    </LegalPageShell>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-bold text-base uppercase tracking-wide" style={{ color: "#13A699" }}>
        {title}
      </h2>
      <div className="mt-2">{children}</div>
    </section>
  );
}
